import { createActionGroup, emptyProps, props } from '@ngrx/store';
import {
  INormalizedEntity,
  INormalizedPayload,
} from '@core/state/entities/entity';
import {
  AnnotationQuestionsKey,
  BulkStudyUpdateJobsKey,
  JoinRequestsKey,
  MembershipsKey,
  ProjectDetailsKey,
  ProjectsKey,
  ReviewerAnnotationStatsKey,
  ReviewerScreeningStatsKey,
  SearchesKey,
  SearchImportJobsKey,
  StagesKey,
} from '../state/entities/entity-name-map';
import {
  ProjectEntityKeys,
  ProjectWithRelatedInvestigatorEntityKeys,
} from '@core/state/entities/project/project.entity';
import { ProjectUsageEntityKeys } from '@core/state/entities/project-usage/project-usage.entity';
import { AddSearchData } from '@app/project/project-overview/create-search/create-search.component';
import {
  AgreementMode,
  CreateStageDto,
  FileUploadSignature,
  InclusionInfoUpdateResults,
  InviteResponse,
  Operation,
  ProjectDbBaseDto,
  RiskOfBiasJobStatus,
  UpdateResultsByProject,
} from '@core/services/api-client.generated';
import type { AnnotationQuestionUpsertRequest } from '@core/services/project/annotation-question-upsert-request';
import { IKeywordCount } from '@core/state/ui/project-index/project-index-ui.reducer';
import { IAnnotationQuestion } from '@core/state/entities/annotation-question/annotation-question.entity';
import { IEditProjectVm } from '@shared/dialogs/edit-project-modal/edit-project.viewmodel';
import { IStage } from '@core/state/entities/stage/stage.entity';
import { ReplacePatch } from 'json-patch';
import {
  SearchImportJobChildKeys,
  SearchImportJobEntityKeys,
} from '@core/state/entities/search-import-job/search-import-job.entity';
import { InvestigatorEntityKeys } from '@core/state/entities/investigator/investigator.entity';
import { FullStatsKeys } from '@core/services/stage/review-effects';
import { InvitationEntityKeys } from '@core/state/entities/invitation/invitation.entity';
import { MembershipChildKeys } from '@core/state/entities/membership/membership.entity';
import {
  ErrorActionProps,
  RequestActionProps,
  SuccessActionProps,
  TriggerActionProps,
} from '@core/state/global-request-state/global-request-state.actions';
import { UpdateSearchData } from '@app/project/project-overview/update-studies/update-studies.component';

export interface EmailInvitationResponseMap {
  [email: string]: InviteResponse;
}

export const projectDetailActions = createActionGroup({
  source: 'Project Api',
  events: {
    'retrieve project reviewer stats': props<
      { projectId: string } & IRequestCorrelationState
    >(),
    'received project reviewer stats': props<
      INormalizedPayload<
        ReviewerScreeningStatsKey | ReviewerAnnotationStatsKey
      > &
        IRequestCorrelationState & { projectId: string }
    >(),
    'error retrieving project reviewer stats':
      props<IErrorStateWithCorrelation>(),
    'retrieve full project stats': props<
      { projectId: string } & IRequestCorrelationState
    >(),
    'full project stats received': props<
      INormalizedPayload<FullStatsKeys> &
        IRequestCorrelationState & {
          projectId: string;
          membershipIds: string[];
          stageIds: string[];
        }
    >(),
    'error retrieving full project stats': props<
      { projectId: string } & IErrorStateWithCorrelation
    >(),
    'agreement mode updated': props<
      INormalizedPayload<ProjectEntityKeys> & SuccessActionProps
    >(),
    'error updating agreement mode': props<
      { projectId: string } & ErrorActionProps
    >(),
    'annotation question repositioned': props<
      {
        projectId: string;
        annotationQuestions: IAnnotationQuestion[];
        movedQuestionId: string;
      } & IRequestCorrelationState
    >(),
    'annotation question reposition failed': props<IProjectRequestErrorState>(),
    'project updated': props<
      INormalizedPayload<ProjectWithRelatedInvestigatorEntityKeys> & {
        projectId: string;
        signalRUpdate: boolean;
      } & { originalProjectDto: ProjectDbBaseDto } & IRequestCorrelationState
    >(),
    'project listing updated': props<
      INormalizedPayload<ProjectWithRelatedInvestigatorEntityKeys> & {
        projectId: string;
        signalRUpdate: boolean;
      }
    >(),
    'delete project': props<
      INormalizedPayload<ProjectEntityKeys> & { projectId: string }
    >(),

    created: props<
      INormalizedPayload<ProjectEntityKeys> & { projectId: string }
    >(),
    registered: props<INormalizedPayload<ProjectEntityKeys>>(),
    'load project listings': props<IRequestCorrelationState>(),
    'project listings loaded': props<
      INormalizedPayload<ProjectWithRelatedInvestigatorEntityKeys> &
        IRequestCorrelationState
    >(),
    'project listings load failed': props<IErrorStateWithCorrelation>(),
    'load pending join request for project': props<{
      projectId: string;
      join;
    }>(),
    'pending join request loaded for project':
      props<INormalizedPayload<JoinRequestsKey>>(),
    'no pending join request for project found': emptyProps(),
    'error retrieving pending join request': props<{ error: any }>(),
    'search import job started': props<
      INormalizedEntity<SearchImportJobEntityKeys> & { searchId: string }
    >(),
    'search import job cancelled':
      props<INormalizedEntity<SearchImportJobsKey, SearchImportJobChildKeys>>(),
    'start bulk study update job': props<
      UpdateSearchData & IRequestCorrelationState & { projectId: string }
    >(),
    's3 signature successfully retrieved for bulk study update job': props<
      IRequestCorrelationState & {
        s3Signature: FileUploadSignature;
        file: File;
        jobId: string;
        projectId: string;
      }
    >(),
    'bulk study update progress updated': props<
      IRequestCorrelationState & {
        jobId: string;
        progress: number | null;
      }
    >(),
    'error retrieving s3 signature for bulk study update job':
      props<IErrorStateWithCorrelation>(),
    'bulk study update job file successfully uploaded to s3': props<
      {
        jobId: string;
      } & IRequestCorrelationState
    >(),
    'error uploading bulk study update job file to s3':
      props<IErrorStateWithCorrelation>(),
    'bulk study update job cancelled':
      props<INormalizedEntity<BulkStudyUpdateJobsKey>>(),
    'delete bulk study update job':
      props<INormalizedEntity<BulkStudyUpdateJobsKey>>(),
    'remove search': props<
      INormalizedEntity<SearchesKey> & { searchId: string }
    >(),
    'delete search import job': props<
      INormalizedEntity<SearchImportJobsKey> & { searchId: string }
    >(),
    'calculate rob': props<{ projectId: string; searchId: string }>(),
    'rob job started': props<{
      jobId: string;
      searchId: string;
      projectId: string;
      status: RiskOfBiasJobStatus;
    }>(),
    'rob job completed': props<{ searchId: string }>(),
    'get study results': props<{ projectId: string; searchId: string }>(),

    'rob job failed': props<{ searchId: string; errorMessage: string }>(),

    'stage added': props<
      INormalizedEntity<StagesKey> &
        IRequestCorrelationState & {
          projectId: string;
          stageId: string;
        }
    >(),

    'annotation question deleted': props<
      INormalizedEntity<StagesKey | AnnotationQuestionsKey> & {
        annotationQuestionId: string;
        projectId: string;
      }
    >(),
    'annotation question edited':
      props<INormalizedEntity<AnnotationQuestionsKey>>(),
    'stage questions update failed': props<INormalizedEntity<StagesKey>>(),
    'stage updated': props<INormalizedEntity<StagesKey>>(),
    'stage update failed': props<{ projectId: string; stageId: string }>(),
    'stage admin write completed': props<{ projectId: string }>(),
    'project favourite set':
      props<INormalizedPayload<ProjectUsageEntityKeys>>(),
    'stage favourite set': props<INormalizedPayload<ProjectUsageEntityKeys>>(),
    'set project favourite': props<{
      projectId: string;
      isFavourite: boolean;
    }>(),
    'update agreement mode': props<
      {
        projectId: string;
        mode: AgreementMode;
      } & TriggerActionProps
    >(),
    'switch current stage': props<{ stageId: string | null }>(),
    'set stage favourite': props<{
      projectId: string;
      stageId: string;
      isFavourite: boolean;
    }>(),
    create: props<{ createVm: IEditProjectVm }>(),
    'reposition annotation question': props<
      {
        projectId: string;
        questionToMoveId: string;
        questionAtDestinationId: string;
      } & IRequestCorrelationState
    >(),
    update: props<
      {
        projectId: string;
        replacePatchOps: ReplacePatch[];
      } & IRequestCorrelationState
    >(),
    'load keywords': emptyProps(),
    'keywords loaded': props<{ keywordCount: IKeywordCount }>(),

    'throttled load projects': props<IRequestCorrelationState>(),
    'project listings page loaded': props<IRequestCorrelationState>(),
    // Each search's upload has its own state (#4658): these name the search they are about.
    'search upload progress update': props<{
      searchId: string;
      progress: number | null;
    }>(),
    'search upload error': props<{
      searchId: string;
      message: string;
      code: number;
    }>(),
    /**
     * S3 accepted this search's reference file: it has left this tab. Not "imported": the server's
     * import job (pushed over the project's live updates) carries the rest. State only, unlike
     * 'search import job started', whose optimistic job row must never reach the store.
     */
    'search reference file sent': props<{ searchId: string }>(),
    /** Clears one search's upload state; no searchId (nothing chosen yet) clears nothing. */
    'reset search upload state': props<{ searchId: string | null | undefined }>(),
    /** Stops this tab's transfer; a server-side import may already be running. */
    'stop search import waiting': props<{ searchId: string }>(),
    'load project': props<
      { projectId: string } & IRequestCorrelationState & RequestActionProps
    >(),
    'retrieve project details': props<
      IProjectRequestCorrelationState & TriggerActionProps
    >(),
    'detail loaded': props<
      INormalizedPayload<ProjectEntityKeys> & {
        projectId: string;
      } & IRequestCorrelationState &
        SuccessActionProps
    >(),
    'project load fail': props<
      IProjectRequestErrorState &
        ErrorActionProps & {
          notFound: boolean;
          /**
           * A reload of a project whose details were already in the store failed transiently (timeout,
           * network, 5xx): its loading state stays Loaded and the data stays visible. Never set for a
           * first load, nor for 404, 401 or 403.
           */
          reload?: boolean;
        }
    >(),
    'start search import': props<{
      addSearchData: AddSearchData;
      file: File;
      /**
       * The project to import into. The search import wizard always sets it, because a run can be
       * retried from another project's page; absent (the legacy dialog), the loaded project is used.
       */
      projectId?: string;
    }>(),
    'project deleted': props<{
      projectId: string;
      signalRUpdate: boolean;
      /** False for a summary-list visibility removal; only physical deletion is terminal. */
      terminalPhysicalDeletion: boolean;
    }>(),
    'project deletion failed': props<IErrorState>(),
    'project remotely deleted': props<{ projectId: string }>(),

    'disable membership': props<{
      projectId: string;
      investigatorId: string;
      correlationId: string;
    }>(),
    'membership disabled successfully': props<{
      projectId: string;
      investigatorId: string;
      correlationId: string;
    }>(),
    'error disabling membership': props<IErrorStateWithCorrelation>(),

    'enable membership': props<{
      projectId: string;
      investigatorId: string;
      correlationId: string;
    }>(),
    'membership enabled successfully': props<{
      projectId: string;
      investigatorId: string;
      correlationId: string;
    }>(),
    'error enabling membership': props<IErrorStateWithCorrelation>(),

    'update project membership groups': props<
      {
        projectId: string;
        investigatorId: string;
        groupIds: string[];
      } & IRequestCorrelationState
    >(),
    'project membership groups updated': props<
      INormalizedEntity<MembershipsKey, MembershipChildKeys> &
        IRequestCorrelationState
    >(),
    'error updating project membership groups':
      props<IErrorStateWithCorrelation>(),
    'search import job deleted': props<
      INormalizedEntity<SearchImportJobsKey> & { searchId: string }
    >(),
    'search import job deletion failed': props<IErrorState>(),
    'search removed': props<
      INormalizedEntity<SearchesKey> & { searchId: string }
    >(),
    'search removal failed': props<IErrorState>(),
    // deletionLifecycle on (reversible deletion S4a): the API recorded a deletion (202). The search stays in the
    // store until project-management unlinks it; the project's SignalR refresh then drops it.
    'search deletion requested': props<{
      searchId: string;
      operationId: string;
      state: string;
    }>(),
    // Reversible deletion S4a (DL-R1S-10): the hub's SearchDeleted push for the open project. The SignalR service has
    // already asked for the project again; the snackbar and the Library refresh react to this.
    'search deleted notification received': props<{
      projectId: string;
      searchId: string;
      searchName: string | null;
      studiesMoved: number;
    }>(),
    refresh: props<IRequestCorrelationState>(),
    'get screening summary': emptyProps(),
    'screening summary received': props<INormalizedEntity<FullStatsKeys>>(),
    'add stage': props<CreateStageDto & IRequestCorrelationState>(),
    'add annotation question': props<
      {
        annotationQuestion: AnnotationQuestionUpsertRequest;
      } & IProjectRequestCorrelationState
    >(),
    'annotation question added successfully': props<
      INormalizedEntity<AnnotationQuestionsKey> &
        IProjectRequestCorrelationState
    >(),
    'error adding annotation question': props<IProjectRequestErrorState>(),
    'copy custom annotation question': props<
      {
        annotationQuestionId: string;
        recursive: boolean;
      } & IProjectRequestCorrelationState
    >(),
    'custom annotation question copied successfully': props<
      {
        affectedQuestionIds: string[];
        copiedQuestionId: string;
      } & INormalizedEntity<AnnotationQuestionsKey> &
        IProjectRequestCorrelationState
    >(),
    'error copying custom annotation question':
      props<IProjectRequestErrorState>(),
    'save custom annotation question': props<
      {
        annotationQuestion: AnnotationQuestionUpsertRequest;
      } & IProjectRequestCorrelationState
    >(),
    'custom annotation question saved successfully': props<
      INormalizedEntity<AnnotationQuestionsKey> &
        IProjectRequestCorrelationState
    >(),
    'error saving custom annotation question':
      props<IProjectRequestErrorState>(),

    'delete annotation question': props<{ questionId: string }>(),
    // Emitted after the workload-shares endpoint accepts a change. The stage entity carries
    // workloadSharesEnabled, which locks the review-mode and reviews-per-study controls, so it
    // must be refreshed without waiting for a project reload.
    'stage workload shares updated': props<{
      stageId: string;
      workloadSharesEnabled: boolean;
    }>(),
    'update stage questions': props<{
      projectId: string;
      stage: IStage;
      stageQuestions: string[];
    }>(),
    'update stage': props<{
      projectId: string;
      stageId: string;
      replacePatchOps: Operation[];
    }>(),
    // Revision-2 README §6.5: one category's administrator guidance at a time. An empty
    // guidance string clears the entry; the server answers with the whole updated stage.
    'update stage category guidance': props<{
      projectId: string;
      stageId: string;
      category: string;
      guidance: string;
    }>(),
    // The project-level default the stages inherit. Same one-category-at-a-time shape; the
    // server answers with the whole updated project.
    'update project category guidance': props<{
      projectId: string;
      category: string;
      guidance: string;
    }>(),
    // `category` names the write this is the terminal action for. Writes are
    // one category at a time and a screen may have several outstanding, so a
    // screen that gates its fields while they save has to release exactly the
    // one that finished — releasing "the" pending category would re-enable a
    // field whose own write is still in flight.
    'project category guidance updated': props<{
      projectId: string;
      category: string;
      categoryGuidance: Record<string, string>;
    }>(),
    // Emitted when the write does not land — the request failed and the
    // administrator dismissed the retry snackbar. It carries no state change;
    // it exists so a screen that gates its guidance field on the write being
    // in flight always has something to release that gate, and can never
    // leave the field disabled with no way back.
    'project category guidance update failed': props<{
      projectId: string;
      category: string;
    }>(),
    // FEAT-025 screening keyword highlighting. One write replaces both lists;
    // the server answers with the normalised lists only.
    'update screening keywords': props<{
      projectId: string;
      inclusionKeywords: string[];
      exclusionKeywords: string[];
    }>(),
    'screening keywords updated': props<{
      projectId: string;
      inclusionKeywords: string[];
      exclusionKeywords: string[];
    }>(),
    // The write did not land (request failed and the retry snackbar was
    // dismissed). No state change; it releases a screen's saving state.
    'screening keywords update failed': props<{
      projectId: string;
    }>(),
    // The stage override's own terminal pair. The stage write also emits the
    // general `stageUpdated`, which carries the whole stage and is what the
    // store needs; these say WHICH guidance write finished, so a screen gating
    // a field is not released by an unrelated stage write.
    'stage category guidance updated': props<{
      projectId: string;
      stageId: string;
      category: string;
    }>(),
    'stage category guidance update failed': props<{
      projectId: string;
      stageId: string;
      category: string;
    }>(),
    'stage questions updated': emptyProps(),
    'load stage': props<{ stageId: string; projectId: string }>(),
    'detect risk of bias': props<{ projectId: string }>(),
    'risk of bias started': emptyProps(),
    'invite members': props<
      {
        projectId: string;
        inviteeEmails: string[];
        groupIds: string[];
        message: string;
      } & IRequestCorrelationState
    >(),
    'members invited response received': props<EmailInvitationResponseMap>(),
    'error inviting members': props<IProjectRequestErrorState>(),
    'respond to join requests': props<
      | ({
          projectId: string;
          investigatorIds: string[];
          approve: true;
          groupIds: string[];
        } & IRequestCorrelationState)
      | ({
          projectId: string;
          investigatorIds: string[];
          approve: false;
        } & IRequestCorrelationState)
    >(),
    'join requests response sent': props<
      INormalizedPayload<ProjectEntityKeys> & {
        projectId: string;
      } & IRequestCorrelationState
    >(),
    'error responding to join requests': props<IProjectRequestErrorState>(),
    'revoke invitations bulk': props<
      {
        projectId: string;
        inviteeEmails: string[];
      } & IRequestCorrelationState
    >(),
    'invitations bulk revoked successfully': props<
      INormalizedPayload<ProjectEntityKeys> & {
        projectId: string;
      } & IRequestCorrelationState
    >(),
    'error bulk revoking invitations': props<IProjectRequestErrorState>(),
    'update project memberships group bulk': props<{
      projectId: string;
      investigatorIds: string[];
      groupIds: string[];
    }>(),
    'project memberships group bulk updated successfully': props<
      INormalizedEntity<MembershipsKey, MembershipChildKeys> & {
        projectId: string;
      }
    >(),
    'error updating project memberships group bulk':
      props<IErrorStateWithCorrelation>(),
    'update invitation groups bulk': props<
      {
        projectId: string;
        inviteeEmails: string[];
        groupIds: string[];
      } & IRequestCorrelationState
    >(),
    'invitation groups bulk updated successfully': props<
      INormalizedEntity<InvitationEntityKeys> & {
        projectId: string;
      } & IRequestCorrelationState
    >(),
    'error bulk updating invitation groups': props<IProjectRequestErrorState>(),
    'resend invitations bulk': props<
      {
        projectId: string;
        inviteeEmails: string[];
        message?: string;
        groupIds?: string[];
      } & IRequestCorrelationState
    >(),
    'invitations bulk resent successfully': props<
      INormalizedPayload<ProjectEntityKeys> & {
        projectId: string;
      } & IRequestCorrelationState
    >(),
    'error bulk resending invitations': props<IProjectRequestErrorState>(),
    'request to join project': props<
      {
        projectId: string;
        message?: string;
      } & IRequestCorrelationState
    >(),
    'error requesting to join project': props<
      IProjectRequestErrorState & IProjectAccessState
    >(),
    'request to join project response received and granted': props<
      {
        projectId: string;
        response: string;
      } & INormalizedPayload<ProjectEntityKeys | InvestigatorEntityKeys> &
        IProjectAccessGrantedState
    >(),
    'request to join project response received': props<
      {
        projectId: string;
        correlationId: string;
        response: string;
      } & INormalizedPayload<ProjectEntityKeys | InvestigatorEntityKeys> &
        IProjectAccessState
    >(),
    'cancel request to join project': props<
      {
        projectId: string;
      } & IRequestCorrelationState
    >(),
    'error cancelling request to join project': props<
      IProjectRequestErrorState & IProjectAccessState
    >(),
    'request to join project cancelled': props<
      {
        projectId: string;
      } & INormalizedPayload<ProjectEntityKeys | InvestigatorEntityKeys> &
        IProjectAccessState
    >(),
    'respond to invitation': props<
      {
        projectId: string;
        accepted: boolean;
        token?: string;
      } & IRequestCorrelationState
    >(),
    'invitation response received': props<
      {
        projectId: string;
      } & INormalizedPayload<ProjectEntityKeys | InvestigatorEntityKeys> &
        IProjectAccessState
    >(),
    'Error responding to invitation': props<
      IProjectRequestErrorState & IProjectAccessState
    >(),
    'retrieve project for invitation': props<
      {
        token: string;
      } & IRequestCorrelationState
    >(),
    'project for invitation retrieved': props<
      {
        projectId: string;
      } & IRequestCorrelationState &
        INormalizedPayload<ProjectEntityKeys | InvestigatorEntityKeys>
    >(),
    'Error retrieving project for invitation':
      props<IErrorStateWithCorrelation>(),
    'update stage permissions': props<
      {
        projectId: string;
        stageId: string;
        permissions: IPermissionInfo[];
      } & IRequestCorrelationState
    >(),
    'stage permissions updated successfully': props<
      INormalizedEntity<StagesKey> & IRequestCorrelationState
    >(),
    'error updating stage permissions': props<IErrorStateWithCorrelation>(),
    'update project permissions': props<
      {
        projectId: string;
        permissions: IPermissionInfo[];
      } & IRequestCorrelationState
    >(),
    'project permissions updated successfully': props<
      INormalizedEntity<ProjectDetailsKey> & IRequestCorrelationState
    >(),
    'error updating project permissions': props<IErrorStateWithCorrelation>(),
    'update project study inclusion info': props<
      { projectId: string; replaceExisting: boolean } & IRequestCorrelationState
    >(),
    'project study inclusion info updated': props<
      {
        projectId: string;
        results: InclusionInfoUpdateResults;
      } & IRequestCorrelationState
    >(),
    'error updating project study inclusion info':
      props<IErrorStateWithCorrelation>(),
    'update all project study inclusion info': props<
      { replaceExisting: boolean } & IRequestCorrelationState
    >(),
    'all project study inclusion info updated': props<
      {
        results: UpdateResultsByProject;
      } & IRequestCorrelationState
    >(),
    'error updating all project study inclusion info':
      props<IErrorStateWithCorrelation>(),
  },
});

export interface IPermissionInfo {
  activity: string;
  allowedProjectGroupIds: string[];
  allowAllProjectMembers: boolean;
}

export interface IRequestCorrelationState {
  correlationId: string;
}

export interface IProjectRequestCorrelationState extends IRequestCorrelationState {
  projectId: string;
}

export interface IProjectAccessState extends IRequestCorrelationState {
  projectId: string;
  accessGranted: boolean;
}

export interface IProjectAccessGrantedState extends IProjectAccessState {
  projectId: string;
  accessGranted: true;
}

export interface IErrorState {
  error: string;
  statusCode?: string;
}

export interface IErrorStateWithCorrelation
  extends IRequestCorrelationState, IErrorState {}

export interface IProjectRequestErrorState extends IErrorStateWithCorrelation {
  projectId: string;
}
